import { refreshAccessToken } from "./refresh-interceptor";
import {
    installExpectedLogoutTracking,
    markExpectedAuthExit,
    reportUnexpectedAuthRedirect,
} from "./auth-redirect-reporter";

/**
 * Client-side inactivity logout (OWASP API2 remediation #2, UX half).
 *
 * The server enforces idle timeout at token-refresh time, but a user parked on a page that
 * makes no authenticated requests (e.g. a catalog served largely by public endpoints) never
 * triggers a refresh, so the server-side check can't promptly log them out. This watches real
 * user activity in the browser and, after `window.bo_idle_ttl` minutes of inactivity, sends the
 * user to logout — which revokes the refresh token server-side and lands them on login.
 *
 * "Activity" = mouse / keyboard / scroll / touch. A full-page navigation naturally resets the
 * timer (the script reloads), so browsing keeps the session alive; only genuine inactivity ends
 * it. Runs only when a session exists on the page, and never on the login page itself.
 *
 * MULTI-TAB: the last-activity timestamp lives in localStorage, so activity in ANY tab counts
 * for ALL tabs — a backgrounded tab won't log you out while another tab is in use. Logout is
 * also synchronised: when one tab clears the session token, the others follow it to login.
 */

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
const CHECK_INTERVAL_MS = 15000;
const PERSIST_THROTTLE_MS = 5000; // don't write to localStorage on every mouse move
const ACTIVITY_KEY = "bo_session_activity";

let lastActivityAt = Date.now();
let lastPersistAt = 0;
let navigatingAway = false;
let refreshingActiveSession = false;

// True while the page is still loading, a navigation is underway, or a user-facing request is
// in flight — we skip the proactive redirect in that window so it can't interrupt an in-progress
// page load / transition / action.
function isPageBusy() {
    if (navigatingAway) {
        return true;
    }
    if (typeof document === "undefined") {
        return false;
    }
    if (document.readyState !== "complete") {
        return true;
    }
    // The bo-components http mixin (showLoading/hideLoading) toggles `loading` on <body> while a
    // request with the loading screen is in flight. Don't redirect out from under it.
    if (document.body && document.body.classList.contains("loading")) {
        return true;
    }
    // #111741: fast reads no longer raise the class right away; the http mixin marks the body
    // busy for the whole in-flight window instead, so the idle redirect still waits for them.
    if (document.body && document.body.dataset.boBusy === "1") {
        return true;
    }
    return false;
}

function tokenKey() {
    return "token_" + window.bo_site;
}

// Exported so pages meant to stay alive while merely visible (e.g. the insights dashboard
// watched as a reference display) can keep the idle clock from advancing — across all tabs.
export function markActivity() {
    const now = Date.now();
    lastActivityAt = now;

    // Persist (throttled) so other tabs see this tab's activity.
    if (now - lastPersistAt >= PERSIST_THROTTLE_MS) {
        lastPersistAt = now;
        try {
            localStorage.setItem(ACTIVITY_KEY, String(now));
        } catch (e) {}
    }
}

function lastActivityAcrossTabs() {
    let shared = 0;
    try {
        shared = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10) || 0;
    } catch (e) {}
    return Math.max(shared, lastActivityAt);
}

function idleLimitMs() {
    const minutes = Number(window.bo_idle_ttl) || 30;
    return minutes * 60 * 1000;
}

function hasSession() {
    try {
        return !!localStorage.getItem(tokenKey());
    } catch (e) {
        return false;
    }
}

/**
 * Read the access token's issued-at time without trusting any of its claims for
 * authorization. The server still performs all authentication; this timestamp is
 * used only to decide when an active browser should send its next keep-alive.
 */
function tokenIssuedAt() {
    try {
        const token = localStorage.getItem(tokenKey());
        const encodedPayload = token && token.split(".")[1];
        if (!encodedPayload) {
            return 0;
        }

        const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
        const padding = "=".repeat((4 - (base64.length % 4)) % 4);
        const payload = JSON.parse(atob(base64 + padding));
        return (Number(payload.iat) || 0) * 1000;
    } catch (e) {
        return 0;
    }
}

function getLogoutUrl() {
    // Shares the refresh route's base + site prefix (…/{site}/refresh-token → …/{site}/logout).
    const refreshUrl = window.bo_refresh_token_url;
    return refreshUrl ? refreshUrl.replace(/\/refresh-token(\?.*)?$/, "/logout") : null;
}

function isOnLoginPage() {
    // Matches any route ending in "login": admin/company (…/login) and consumer
    // (…/customer-login). Used to keep the idle watcher AND the cross-tab storage-logout
    // listener off the login page — where they caused two login tabs to reload each other.
    try {
        return /login\/?$/i.test(window.location.pathname);
    } catch (e) {
        return false;
    }
}

function goToLogout(reasonCode = "idle_timeout") {
    markExpectedAuthExit(reasonCode);
    try {
        localStorage.removeItem(tokenKey());
    } catch (e) {}

    const logoutUrl = getLogoutUrl();
    if (logoutUrl) {
        window.location.assign(logoutUrl);
    }
}

/**
 * Browser input resets the client idle timer, but it does not by itself rotate the
 * server-side refresh token. Keep the two clocks aligned while the user is active;
 * otherwise an active user on a mostly public page can cross the server idle limit
 * and be logged out on their next protected request.
 */
function refreshActiveSession() {
    if (refreshingActiveSession || document.visibilityState !== "visible") {
        return;
    }

    const now = Date.now();
    if (now - lastActivityAcrossTabs() >= idleLimitMs()) {
        return;
    }

    // Refresh halfway through the idle window. setAuthToken() stores the newly issued
    // JWT, so its `iat` also coordinates this heartbeat across browser tabs.
    if (now - tokenIssuedAt() < idleLimitMs() / 2) {
        return;
    }

    refreshingActiveSession = true;
    refreshAccessToken()
        .catch((error) => {
            // A 401 is definitive (expired/revoked refresh cookie). Network failures are
            // transient, so leave the user in place and retry on a later interval.
            if (error && error.response && error.response.status === 401) {
                const refreshErrors = error.response.data && error.response.data.errors
                    ? error.response.data.errors
                    : {};
                const reasonCode = refreshErrors.reason_code || "missing_cookie";
                reportUnexpectedAuthRedirect({
                    eventId: refreshErrors.event_id || error.boAuthEventId,
                    source: "refresh_401",
                    reasonCode,
                    failedRequestUrl: window.location.pathname,
                    httpStatus: 401,
                    token: (() => {
                        try {
                            return localStorage.getItem(tokenKey());
                        } catch (e) {
                            return null;
                        }
                    })(),
                });
                goToLogout(reasonCode);
            }
        })
        .finally(() => {
            refreshingActiveSession = false;
        });
}

export function startIdleWatch() {
    // Only meaningful where there's a session context (site + refresh endpoint configured),
    // and never on the login page.
    if (!window.bo_refresh_token_url || isOnLoginPage()) {
        return;
    }

    installExpectedLogoutTracking();

    // Seed activity for this freshly loaded tab (loading a page is itself an action).
    markActivity();

    // A navigation is starting → treat the page as busy so the timer won't redirect mid-load.
    window.addEventListener("beforeunload", () => {
        navigatingAway = true;
    });

    ACTIVITY_EVENTS.forEach((evt) =>
        window.addEventListener(evt, markActivity, { passive: true, capture: true })
    );

    // If another tab logs out (clears the shared token), follow it to login.
    //
    // Re-read localStorage after a short delay so rapid browser storage churn cannot be
    // mistaken for a real logout.
    window.addEventListener("storage", (e) => {
        if (e.key !== tokenKey() || e.newValue !== null || isOnLoginPage()) {
            return;
        }
        setTimeout(() => {
            if (hasSession() || isOnLoginPage()) {
                return; // token came back (login/refresh churn) — not a logout
            }
            const logoutUrl = getLogoutUrl();
            if (logoutUrl) {
                window.location.assign(logoutUrl);
            }
        }, 300);
    });

    setInterval(() => {
        if (!hasSession()) {
            return;
        }
        // Don't interrupt an in-progress page load / navigation — retry on the next tick.
        if (isPageBusy()) {
            return;
        }
        // Idle only if EVERY tab has been idle past the window (activity is shared via storage).
        if (Date.now() - lastActivityAcrossTabs() < idleLimitMs()) {
            refreshActiveSession();
            return;
        }
        goToLogout();
    }, CHECK_INTERVAL_MS);
}
